import { createTheme } from '@mui/material/styles';
import { semanticTokens } from './tokens';

/**
 * Application Material UI v5 theme configuration.
 * Consumes semantic tokens for identity-specific overrides while delegating
 * breakpoints, elevation, action states, and baseline scales to native MUI.
 */
export const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: semanticTokens.primary,
    success: {
      main: semanticTokens.status.positive,
      light: semanticTokens.status.positiveLight,
      dark: semanticTokens.status.positiveDark,
    },
    error: {
      main: semanticTokens.status.negative,
      light: semanticTokens.status.negativeLight,
      dark: semanticTokens.status.negativeDark,
    },
    warning: {
      main: semanticTokens.status.warning,
      light: semanticTokens.status.warningLight,
      dark: semanticTokens.status.warningDark,
    },
    info: {
      main: semanticTokens.status.info,
    },
    background: {
      default: semanticTokens.surfaces.base,
      paper: semanticTokens.surfaces.dark,
    },
    action: {
      hover: semanticTokens.action.hover,
      selected: semanticTokens.action.selected,
      focus: semanticTokens.action.focus,
    },
    divider: semanticTokens.borders.divider,
    text: {
      primary: semanticTokens.text.primary,
      secondary: semanticTokens.text.secondary,
      disabled: semanticTokens.text.disabled,
    },
    surfaces: semanticTokens.surfaces,
    borders: semanticTokens.borders,
    status: semanticTokens.status,
    brand: semanticTokens.brand,
  },
  typography: {
    fontFamily: semanticTokens.typography.fontFamily,
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: semanticTokens.shape.borderRadius,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: semanticTokens.surfaces.base,
          color: semanticTokens.text.primary,
          fontFamily: semanticTokens.typography.fontFamily,
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: false,
      },
      styleOverrides: {
        root: {
          userSelect: 'none',
          WebkitUserSelect: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: semanticTokens.shape.borderRadius,
          textTransform: 'none',
          fontWeight: 500,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: semanticTokens.surfaces.dark,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: semanticTokens.surfaces.dark,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: semanticTokens.borders.default,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: semanticTokens.brand.primaryLight,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: semanticTokens.brand.primary,
            borderWidth: semanticTokens.borders.width.medium,
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: semanticTokens.surfaces.elevated,
          border: `1px solid ${semanticTokens.borders.default}`,
          backgroundImage: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: semanticTokens.surfaces.card,
          border: `1px solid ${semanticTokens.borders.default}`,
          borderRadius: semanticTokens.shape.borderRadius,
          backgroundImage: 'none',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: semanticTokens.surfaces.subtle,
          border: `1px solid ${semanticTokens.borders.default}`,
          borderRadius: semanticTokens.shape.borderRadius,
          fontSize: '0.75rem',
          padding: '8px 12px',
        },
        arrow: {
          color: semanticTokens.surfaces.subtle,
          '&::before': {
            border: `1px solid ${semanticTokens.borders.default}`,
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: semanticTokens.borders.divider,
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        thumb: {
          userSelect: 'none',
          WebkitUserSelect: 'none',
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        thumb: {
          userSelect: 'none',
          WebkitUserSelect: 'none',
        },
      },
    },
  },
});

export default theme;
